import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Award, BookOpen, Clock, FileSpreadsheet, FileText, LayoutDashboard, LogOut, Menu, Phone, ShoppingBag, Truck, User as UserIcon, Users } from 'lucide-react'
import { useAuth } from '../context/Auth'
import { useCart } from '../context/Cart'
import { useShop } from '../context/Shop'
import { COMPANY, eur, ttc } from '../lib/format'
import SearchBox from './SearchBox'

export const Logo = () => (
  <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0" aria-label="Carper Sports - Accueil" data-testid="header-logo">
    <div className="w-10 h-10 sm:w-11 sm:h-11 shrink-0 bg-gradient-to-br from-brand-orange to-red-600 rounded-xl flex items-center justify-center font-display font-bold text-2xl text-white shadow-lg shadow-orange-500/30 group-hover:scale-105 transition-transform">CS</div>
    <div className="flex flex-col">
      <span className="font-display text-xl sm:text-2xl tracking-wider font-bold bg-gradient-to-r from-white via-slate-100 to-slate-400 bg-clip-text text-transparent">CARPER<span className="text-brand-orange">SPORTS</span></span>
      <span className="hidden sm:block text-[10px] text-slate-400 tracking-widest uppercase font-semibold">Équipementier Officiel</span>
    </div>
  </Link>
)

function AccountMenu() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const handler = (ev: MouseEvent) => {
      if (!ref.current?.contains(ev.target as Node)) setOpen(false)
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])
  const item = 'flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-brand-orange w-full text-left'

  // Structure DOM constante (pas d'early-return) pour éviter les erreurs de réconciliation
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => (user ? setOpen((v) => !v) : navigate('/connexion'))}
        className="p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-2"
        aria-label={user ? 'Mon compte' : 'Se connecter'}
        data-testid="header-account-button"
      >
        {user && user.picture
          ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
          : <UserIcon className="w-6 h-6" />}
        {user && <span className="hidden xl:inline text-xs font-semibold max-w-[110px] truncate">{user.first_name || user.email}</span>}
      </button>
      {open && user && (
        <div
          className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50"
          onClick={() => setOpen(false)}
          data-testid="header-account-menu"
        >
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-xs text-slate-400">Connecté</p>
            <p className="text-sm font-bold text-navy-900 truncate">{user.email}</p>
          </div>
          <Link to="/compte" className={item} data-testid="menu-mon-espace"><UserIcon className="w-4 h-4" /> Mon espace</Link>
          {user.role === 'admin' && <Link to="/admin" className={item} data-testid="menu-admin"><LayoutDashboard className="w-4 h-4" /> Back-office</Link>}
          <button type="button" className={item} onClick={async () => { await logout(); navigate('/') }} data-testid="menu-logout"><LogOut className="w-4 h-4" /> Se déconnecter</button>
        </div>
      )}
    </div>
  )
}

function Navbar({ mobileOpen }: { mobileOpen: boolean }) {
  const { menu } = useShop()
  const [openId, setOpenId] = useState<string | null>(null)
  const location = useLocation()
  useEffect(() => { setOpenId(null) }, [location])
  useEffect(() => {
    const close = (e: MouseEvent) => !(e.target as HTMLElement).closest('.cs-menu') && setOpenId(null)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [])
  return (
    <nav className={`cs-navbar ${mobileOpen ? 'is-open' : ''}`} aria-label="Navigation principale" data-testid="main-navbar">
      <div className="cs-navbar__inner">
        <ul className="cs-menu">
          {menu.map((entry) => (
            <li key={entry.id} className={openId === entry.id ? 'open' : ''}>
              <button type="button" aria-haspopup="true" aria-expanded={openId === entry.id} onClick={() => setOpenId(openId === entry.id ? null : entry.id)} data-testid={`nav-${entry.id}`}>
                {entry.label} <span className="cs-arrow" />
              </button>
              {entry.columns ? (
                <div className="cs-dropdown cs-mega" style={{ ['--cs-cols' as string]: Math.min(6, Math.max(1, entry.columns.length)) }}>
                  {entry.columns.map((c) => (
                    <div key={c.accent} className="cs-col" data-accent={c.accent} style={{ ['--cs-col-accent' as string]: c.color }}>
                      <h3><Link to={c.to}>{c.title}</Link></h3>
                      <ul>{c.items.map((l) => <li key={l.to}><Link to={l.to}>{l.label}</Link></li>)}</ul>
                    </div>
                  ))}
                </div>
              ) : (
                <ul className="cs-dropdown">{entry.items!.map((l) => <li key={l.to}><Link to={l.to} data-testid={`nav-item-${l.to.replace(/\//g,'-').replace(/^-/,'')}`}>{l.label}</Link></li>)}</ul>
              )}
            </li>
          ))}
        </ul>
        <Link to="/catalogues" className="cs-navbar__cta" title="Catalogues Partenaires" data-testid="nav-catalogues"><BookOpen /> <span>Catalogues Partenaires</span></Link>
        <Link to="/services-clubs" className="cs-navbar__cta cs-navbar__cta--clubs" title="Services Clubs & Associations" data-testid="nav-services-clubs"><Users /> <span>Clubs & Associations</span></Link>
      </div>
    </nav>
  )
}

export default function Header() {
  const { totals, setOpen } = useCart()
  const { user } = useAuth()
  const { meta } = useShop()
  const [mobile, setMobile] = useState(false)
  const location = useLocation()
  useEffect(() => { setMobile(false) }, [location])
  return (
    <div className="no-print">
      <div className="bg-navy-950 text-slate-300 text-xs py-2 px-4 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5 text-brand-green font-medium"><Truck className="w-3.5 h-3.5" /> Livraison offerte dès {eur(ttc(meta?.shop.freeShippingHT ?? 150))} TTC</span>
            <span className="hidden md:flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-brand-orange" /> Devis express sous 24h</span>
            <span className="hidden lg:flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-yellow-400" /> Tarifs dégressifs pour clubs & associations</span>
          </div>
          <div className="flex items-center gap-4">
            <a href={COMPANY.phoneHref} className="hover:text-white flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {COMPANY.phone}</a>
            {user && (
              <>
                <span className="text-slate-700">|</span>
                <Link to="/compte" className="hover:text-white flex items-center gap-1 font-semibold" data-testid="topbar-account-link"><UserIcon className="w-3.5 h-3.5" /> Mon espace</Link>
              </>
            )}
            <span className="hidden sm:inline text-slate-700">|</span>
            <Link to="/devis" className="hidden sm:flex hover:text-white items-center gap-1 text-brand-orange font-semibold"><FileText className="w-3.5 h-3.5" /> Service client & devis</Link>
          </div>
        </div>
      </div>
      <header className={`sticky top-0 z-40 glass-header text-white border-b border-slate-800 shadow-xl ${mobile ? 'cs-header-open' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 gap-4">
            <Logo />
            <div className="hidden lg:flex flex-1 max-w-xl mx-4"><SearchBox id="nav-search" /></div>
            <div className="flex items-center gap-1 sm:gap-3 shrink-0">
              <Link to="/devis" className="hidden md:flex items-center gap-2 bg-brand-ink hover:bg-orange-800 text-white text-xs font-bold px-4 py-2.5 rounded-lg shadow-md shadow-orange-500/20 transition-all hover:-translate-y-0.5" data-testid="header-devis-button">
                <FileSpreadsheet className="w-4 h-4" /><span>Devis Express</span>
              </Link>
              <AccountMenu />
              <button type="button" onClick={() => setOpen(true)} className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white px-3 sm:px-3.5 py-2 rounded-lg transition-colors" aria-label="Ouvrir le panier" data-testid="header-cart-button">
                <span className="relative">
                  <ShoppingBag className="w-5 h-5 text-brand-green" />
                  <span className="absolute -top-2 -right-2 bg-brand-ink text-white text-[10px] font-bold min-w-4 h-4 px-0.5 rounded-full flex items-center justify-center border-2 border-navy-900" data-testid="header-cart-count">{totals.count}</span>
                </span>
                <span className="text-xs font-bold hidden sm:inline" data-testid="header-cart-total">{eur(totals.total_ttc)}</span>
              </button>
              <button type="button" onClick={() => setMobile(!mobile)} className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg" aria-label="Menu" data-testid="header-mobile-menu-button"><Menu className="w-6 h-6" /></button>
            </div>
          </div>
          {mobile && (
            <div className="lg:hidden pb-4 space-y-3">
              <SearchBox id="mobile-search" />
              <div className="grid grid-cols-2 gap-2 text-xs font-semibold">
                <Link to="/devis" className="flex items-center gap-2 bg-brand-ink px-3 py-2.5 rounded-lg"><FileSpreadsheet className="w-4 h-4" /> Devis Express</Link>
                <Link to={user ? '/compte' : '/connexion'} className="flex items-center gap-2 bg-slate-800 border border-slate-700 px-3 py-2.5 rounded-lg"><UserIcon className="w-4 h-4 text-brand-orange" /> {user ? 'Mon espace' : 'Connexion'}</Link>
              </div>
            </div>
          )}
        </div>
        <Navbar mobileOpen={mobile} />
      </header>
    </div>
  )
}
